import React, { useState } from "react";
import { Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import usePageMeta from "@/lib/seo";

export default function ContactPage() {
  usePageMeta({ title: "Contact", description: "Pitch us, submit a product, or just say hello — Gulf Coast Home Co." });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-3">Get in touch</p>
        <h1 className="font-heading text-4xl md:text-5xl text-[#2D3032] font-light tracking-tight mb-6">
          Pitch us. Or just say hi.
        </h1>
        <p className="text-[#636769] leading-relaxed mb-10">
          Brand collabs, product submissions, journal pitches, or porch-rocker recommendations — we read everything.
        </p>
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <Mail size={18} className="text-[#8EAD9E] mt-1" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#636769]">Email</p>
              <p className="text-[#2D3032]">gulfcoasthomeco@gmail.com</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MapPin size={18} className="text-[#8EAD9E] mt-1" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#636769]">Based</p>
              <p className="text-[#2D3032]">Pensacola — Tampa — Apalachicola</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="md:col-span-7 bg-white border border-[#E6E1D6] p-8 md:p-10 space-y-6" data-testid="contact-form">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#636769] mb-2 block">Name</label>
          <input
            required
            data-testid="contact-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border-b border-[#E6E1D6] focus:border-[#2D3032] outline-none py-2 bg-transparent text-[#2D3032]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#636769] mb-2 block">Email</label>
          <input
            required
            type="email"
            data-testid="contact-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border-b border-[#E6E1D6] focus:border-[#2D3032] outline-none py-2 bg-transparent text-[#2D3032]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#636769] mb-2 block">Message</label>
          <textarea
            required
            rows={5}
            data-testid="contact-message-input"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full border-b border-[#E6E1D6] focus:border-[#2D3032] outline-none py-2 bg-transparent text-[#2D3032] resize-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit-btn"
          className="bg-[#2D3032] text-white px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#8EAD9E] transition-colors disabled:opacity-50"
        >
          {loading ? "Sending..." : "Send message"}
        </button>
      </form>
    </div>
  );
}
